import React, { useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from "@mui/material/Container";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Snackbar,
    TextField,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Header from "../headers/Header";

import AddPlayerModal from '../modals/AddPlayerModal';
import RosterCard from '../components/RosterCard';
import LoadingShells from '../utilComponents/LoadingShells';

import { fetchWithRetry, fetchRoster } from '../utils/ApiHelper'
import { useRoster } from '../utils/useRoster';


const Constants = require("../constants/Constants");
let IMAGE_URL = Constants.IMAGE_URL;
let CHARACTERS = Constants.CHARACTERS;

const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Roster = () => {
    const { roster: rosterData, setRoster: setRosterData } = useRoster();
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleOpen = () => setModalOpen(true);
    const handleClose = () => setModalOpen(false);

    const alertInit = {
        "show": false,
        "messages": ""
    };

    const successInit = {
        "show": false,
        "messages": ""
    };

    const [alert, setAlert] = useState(alertInit);
    const [success, setSuccess] = useState(successInit);
    const [searchTerm, setSearchTerm] = useState(""); // State to hold search term

    // Filter function to check partial match
    const filterRoster = (term) => {
        const formattedTerm = term.toLowerCase().replace(/\s+/g, ""); // Remove spaces and lowercase
        return rosterData.filter(player => {
            const playerFullName = player.firstName + ' ' + player.lastName
            const fullName = playerFullName.toLowerCase().replace(/\s+/g, "");
            return fullName.includes(formattedTerm);
        });
    };

    const filteredRoster = filterRoster(searchTerm); // Filter roster based on search term

    async function submitNewPlayer(playerData) {

        setLoading(true);

        const headers = {
            'Content-Type': 'application/json'
        };

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(playerData)
        };

        try {
            const response = await fetchWithRetry(`${process.env.REACT_APP_BACKEND_ADDRESS || ""}/api/players`, requestOptions);
            const res = await response.json();

            // Handle success and failure cases based on response data
            if (response.ok) {
                setSuccess({ show: true, messages: `${res.firstName} ${res.lastName} was successfully added to roster.` });
                handleClose()
                setLoading(false)
                try {
                    const roster = await fetchRoster();
                    localStorage.setItem("apiRosterV2", JSON.stringify(roster));
                    setRosterData(roster)
                } catch (rosterError) {
                    console.log("Roster fetch error", rosterError);
                    setAlert({ show: true, messages:"Failed to fetch roster."});
                }
            }
            else {
                const errorMessage = `Request failed with status ${response.status}`;
                console.error(errorMessage);
                setAlert({ show: true, messages: errorMessage });
                setLoading(false)
            }

        } catch (error) {
            console.error("Error submitting player:", error);
            setLoading(false)

            // Set alert for network or unexpected errors
            setAlert({ show: true, messages: ["An error occurred. Please try again. "] });

        }
    }


    const handleAlertClose = () => {
        setAlert(alertInit);
    };

    const handleSuccessClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccess({ "show": false, "messages": "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let playerData = {
            "firstName": e.target[0].value,
            "lastName": e.target[2].value
        };
        e.target.reset();
        await submitNewPlayer(playerData);
    };

    return (
        <Container
            component="main"
            sx={{
                marginTop: 4,
                flexDirection: "column",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >{loading ? (
            <LoadingShells />
        ) : (<>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ p: 1, mb: 3 }}
            >
                <Header src={`${IMAGE_URL}headers/Roster.png`} alt="Roster Title" />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '80%', mb: { xs: 4, md: 6 } }}>
                <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Search Current Roster"
                            variant="filled"
                            fullWidth
                            sx={{ bgcolor: 'white', border: 3 }}
                            onChange={(e) => setSearchTerm(e.target.value)} // Update search term on change
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box display='flex' justifyContent={{ xs: 'center', md: 'none' }} width="100%" >
                            <Button
                                variant="contained"
                                sx={{
                                    width: { xs: '70%', sm: '100%' },
                                    marginTop: { xs: 2, sm: 0 },
                                    border: 3,
                                    borderColor: "black"
                                }}
                                onClick={handleOpen}
                            >
                                <img
                                    loading="lazy"
                                    src={`${IMAGE_URL}headers/AddPlayer.png`}
                                    style={{ width: '90%' }}
                                    alt="Add Player"
                                />
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <AddPlayerModal open={modalOpen} handleClose={handleClose} handleSubmit={handleSubmit} />

            <Snackbar open={success.show} autoHideDuration={6000} onClose={handleSuccessClose}>
                <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
                    {success.messages}
                </Alert>
            </Snackbar>

            <Dialog
                open={alert.show}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Data Entry Errors"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alert.messages}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAlertClose} autoFocus>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Roster Cards Grid */}
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                {filteredRoster?.map((player, index) => (
                    <Grid item xs={6} sm={4} md={4} key={index}>
                        <RosterCard
                            onClick={() => navigate(`/stats/players/${player.playerId}`)} // Use navigate to go to player page
                            firstName={player.firstName}
                            lastName={player.lastName}
                            imageUrl={`${IMAGE_URL}${CHARACTERS[parseInt(player.characterId) - 1]['rosterUrl']}`}
                            startcolor={`${CHARACTERS[parseInt(player.characterId) - 1]['startColor']}`}  // Dark color
                            endcolor={`${CHARACTERS[parseInt(player.characterId) - 1]['endColor']}`}     // Light color
                        />
                    </Grid>
                ))}
            </Grid>
        </>
        )}
        </Container>
    );
};

export default Roster;
